import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { TestStep } from './test-executor.service';
import { ExecutionResultsApiService, CreateExecutionResultRequest, ExecutionResult } from './execution-results-api.service';
import { environment } from '../../../src/environments/environment';
import { Client } from './test-accelerator.service';

export interface ExecutionResultRecord {
  stepId: string;
  stepUuid?: string;  // UUID from step object (id field from StepObject)
  action: string;
  description: string;
  status: 'success' | 'failed' | 'stopped' | 'running';
  passed?: boolean;  // For verification/assertion actions
  result?: any;
  error?: string;
  screenshot?: string;  // Base64 encoded screenshot
  timestamp: string;
  duration?: number;  // in milliseconds
  sessionId?: string;  // Optional: to group results by session
  packageId?: string;  // Deprecated: use functionalAreaId for saving
  functionalAreaId?: string;  // Preferred: functional area for database saving
  clientPackageMappingId?: string; // Ensure this property exists
  scenarioId?: string; // Add scenario ID for database saving
  executionRunId?: string; // UUID identifying one execution of a specific scenario
  tenantId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExecutionResultService {
  private results: ExecutionResultRecord[] = [];
  private currentSessionId: string | null = null;
  private currentTestRunId: string | null = null;
  private currentPackageId: string | null = null; // kept for backward compatibility
  private currentFunctionalAreaId: string | null = null;
  private currentScenarioId: string | null = null;
  private currentClientPackageMappingId: string | null = null;
  private currentTenantId: string | null = null;

  // Observable to notify components when results change
  private resultsSubject = new BehaviorSubject<ExecutionResultRecord[]>([]);
  public results$ = this.resultsSubject.asObservable();

  // Track pending save operations to ensure flush before teardown
  private pendingSaves: Promise<any>[] = [];

  constructor(
    private executionResultsApiService: ExecutionResultsApiService,
    private router: Router, private http: HttpClient,
    private activatedRoute: ActivatedRoute
  ) {
    // Removed localStorage loading - results will be fetched from database
    this.resultsSubject.next(this.results);
    this.initializeUrlParameterWatcher();
  }

  /**
   * Override API base for execution results saving (used in Electron runner cloud mode)
   */
  public setApiBase(apiBase: string): void {
    try {
      const base = apiBase.replace(/\/$/, '');
      this.executionResultsApiService.setBaseUrl(`${base}/execution-results`);
      try { console.log('[ExecutionResultService] Base URL overridden to:', `${base}/execution-results`); } catch { }
    } catch { }
  }


  public getReferenceList(codeKeys: string[]) {
    return this.http.post<any[]>(`${environment.apiUrl}/reference-values/by-code-keys`, { codeKeys });
  }

  getExecutionResultWithFilters(filterPayload: any): Observable<ExecutionResult[]> {
    return this.http.post<ExecutionResult[]>(
      `${environment.apiUrl}/execution-results/filter`,
      filterPayload
    );
  }

  /**
   * Get scenario summary with functional area tabs and pagination
   */
  getScenarioSummary(filterPayload: any, page: number = 1, pageSize: number = 10): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/execution-results/scenario-summary`,
      {
        ...filterPayload,
        page,
        pageSize
      }
    );
  }

  public getExecutionResult(): Observable<ExecutionResult[]> {
    return this.http.get<ExecutionResult[]>(
      `${environment.apiUrl}/execution-results`
    );
  }

  public getAllPackages(): Observable<any> {
    return this.http.get<any[]>(`${environment.apiUrl}/packages`);
  }


   public getAllPackagesById(clientId: string): Observable<any> {
    return this.http.get<any[]>(`${environment.apiUrl}/client-package-mapping/client/${clientId}`);
  }


  getAllClients(): Observable<Client[]> {
    return this.http.get<Client[]>(`${environment.apiUrl}/clients`);
  }

  /**
   * Initialize URL parameter watcher to automatically extract packageId and scenarioId
   */
  private initializeUrlParameterWatcher(): void {
    // Watch for navigation events to update context from URL
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.extractUrlParameters();
    });

    // Also extract on initialization
    this.extractUrlParameters();
  }

  /**
   * Extract functionalAreaId and scenarioId from current URL
   */
  private extractUrlParameters(): void {
    try {
      const url = this.router.url;
      // Pattern for scenario/capture/:functionalAreaId/:scenarioId
      const captureRouteMatch = url.match(/\/scenario\/capture\/([^\/]+)\/([^\/]+)/);

      if (captureRouteMatch) {
        const functionalAreaId = captureRouteMatch[1];
        const scenarioId = captureRouteMatch[2];

        this.setExecutionContext(functionalAreaId, scenarioId);
      }
    } catch (error) {
    }
  }



  /**
   * Generate a unique test run ID
   */
  private generateTestRunId(): string {
    return `test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Start a new execution session
   */
  startSession(packageId?: string, scenarioId?: string, clientPackageMappingId?: string, tenantId?: string): string {
    this.currentSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.currentTestRunId = this.generateTestRunId();
    // Treat the first parameter as functionalAreaId going forward
    this.currentFunctionalAreaId = packageId || null;
    this.currentClientPackageMappingId = clientPackageMappingId || null;
    this.currentTenantId = tenantId || null;
    this.currentPackageId = packageId || null;
    this.currentScenarioId = scenarioId || null;
    return this.currentSessionId;
  }

  /**
   * Set current package and scenario context
   */
  setExecutionContext(packageId: string, scenarioId: string, clientPackageMappingId?: string, tenantId?: string): void {
    this.currentFunctionalAreaId = packageId;
    this.currentPackageId = packageId;
    this.currentScenarioId = scenarioId;
    this.currentClientPackageMappingId = clientPackageMappingId || null;
    this.currentTenantId = tenantId || null;
      }

  /**
   * Force extraction of URL parameters (can be called manually by components)
   */
  public updateContextFromUrl(): void {
    this.extractUrlParameters();
  }

  /**
   * Get current execution context
   */
  public getCurrentContext(): { packageId: string | null; scenarioId: string | null; clientPackageMappingId: string | null } {
    return {
      packageId: this.currentFunctionalAreaId || this.currentPackageId,
      scenarioId: this.currentScenarioId,
      clientPackageMappingId: this.currentClientPackageMappingId
    };
  }

  /**
  
   */
  public loadExecutionResultsFromDatabase(packageId: string): Observable<ExecutionResult[]> {
    return this.executionResultsApiService.getByPackage(packageId);
  }

  /**
   * Load execution results from database for a specific scenario
   */
  public loadExecutionResultsByScenario(scenarioId: string): Observable<ExecutionResult[]> {
    return this.executionResultsApiService.getByScenario(scenarioId);
  }

  /**
   * Load execution results from database for a specific execution run
   */
  public loadExecutionResultsByRunId(executionRunId: string): Observable<ExecutionResult[]> {
    return this.executionResultsApiService.getByRunId(executionRunId);
  }

  /**
   * End the current session
   */
  endSession(): void {
    this.currentSessionId = null;
    this.currentPackageId = null;
    this.currentScenarioId = null;
    this.currentClientPackageMappingId = null;
    this.currentTenantId = null;
  }

  /**
   * Add a new execution result
   */
  addResult(result: ExecutionResultRecord): void {

    // Don't store 'wait' actions as per requirement
    if (result.action.toLowerCase() === 'wait') {
      return;
    }

    // Generate new test run ID if not in a session or starting fresh
    if (!this.currentTestRunId) {
      this.currentTestRunId = this.generateTestRunId();
    }

    // Add test run ID and session ID
    // result.testRunId = this.currentTestRunId;
    if (this.currentSessionId) {
      result.sessionId = this.currentSessionId;
    }

    // Add functional area and scenario IDs if available
    if (this.currentFunctionalAreaId) {
      result.functionalAreaId = this.currentFunctionalAreaId;
    }
    if (this.currentScenarioId) {
      result.scenarioId = this.currentScenarioId;
    }
    // Always assign clientPackageMappingId and tenantId from context (not conditional)
    result.clientPackageMappingId = this.currentClientPackageMappingId || undefined;
    result.tenantId = this.currentTenantId || undefined;

    this.results.push(result);
    // Removed localStorage save - only save to database
    this.saveExecutionResult(result); // Save to database
    this.resultsSubject.next([...this.results]); // Notify subscribers
  }

  /**
   * Add execution result with explicit context (for parallel chain execution).
   * Unlike addResult(), this does NOT read from singleton fields — it uses the provided context.
   */
  addResultWithContext(
    result: ExecutionResultRecord,
    context: { functionalAreaId?: string; scenarioId?: string; clientPackageMappingId?: string; executionRunId?: string; tenantId?: string }
  ): void {
    // Don't store 'wait' actions as per requirement
    if (result.action.toLowerCase() === 'wait') {
      return;
    }

    // Generate new test run ID if not in a session or starting fresh
    if (!this.currentTestRunId) {
      this.currentTestRunId = this.generateTestRunId();
    }

    // Add session ID if available
    if (this.currentSessionId) {
      result.sessionId = this.currentSessionId;
    }

    // Stamp context from explicit parameters (NOT singleton fields)
    if (context.functionalAreaId) {
      result.functionalAreaId = context.functionalAreaId;
    }
    if (context.scenarioId) {
      result.scenarioId = context.scenarioId;
    }
    result.clientPackageMappingId = context.clientPackageMappingId || undefined;
    if (context.executionRunId) {
      result.executionRunId = context.executionRunId;
    }
    result.tenantId = context.tenantId || this.currentTenantId || undefined;

    this.results.push(result);
    this.saveExecutionResult(result); // Save to database
    this.resultsSubject.next([...this.results]); // Notify subscribers
  }

  /**
   * NEW: Log precondition execution result
   * Creates a special execution result record for precondition phase
   */
  logPreconditionExecution(preconditionResult: {
    success: boolean;
    authSuccess?: boolean;
    screenValidated?: boolean;
    error?: string;
    duration: number;
  }, executionContext?: { functionalAreaId?: string; scenarioId?: string; clientPackageMappingId?: string; executionRunId?: string; tenantId?: string }): void {
    console.log('[ExecutionResultService] Logging precondition execution:', preconditionResult);

    // Create a special result record for preconditions
    const result: ExecutionResultRecord = {
      stepId: 'precondition',
      stepUuid: 'precondition-' + Date.now(),
      action: 'precondition',
      description: 'Pre-condition setup (authentication + screen navigation)',
      status: preconditionResult.success ? 'success' : 'failed',
      passed: preconditionResult.success,
      result: {
        authSuccess: preconditionResult.authSuccess,
        screenValidated: preconditionResult.screenValidated
      },
      error: preconditionResult.error,
      timestamp: new Date().toISOString(),
      duration: preconditionResult.duration,
      sessionId: this.currentSessionId || undefined,
      functionalAreaId: executionContext?.functionalAreaId || this.currentFunctionalAreaId || undefined,
      scenarioId: executionContext?.scenarioId || this.currentScenarioId || undefined,
      clientPackageMappingId: executionContext?.clientPackageMappingId || this.currentClientPackageMappingId || undefined,
      executionRunId: executionContext?.executionRunId || undefined,
      tenantId: executionContext?.tenantId || this.currentTenantId || undefined
    };

    // Add to results for UI display
    this.results.push(result);

    // Save to database with precondition metadata
    this.saveExecutionResultWithPrecondition(result, preconditionResult);

    this.resultsSubject.next([...this.results]);
  }

  /**
   * Get all execution results
   */
  getAllResults(): ExecutionResultRecord[] {
    return [...this.results];
  }

  /**
   * Get results for a specific session
   */
  getResultsBySession(sessionId: string): ExecutionResultRecord[] {
    return this.results.filter(r => r.sessionId === sessionId);
  }

  /**
   * Get results for the current session
   */
  getCurrentSessionResults(): ExecutionResultRecord[] {
    if (!this.currentSessionId) return [];
    return this.getResultsBySession(this.currentSessionId);
  }

  /**
   * Clear all results
   */
  clearAllResults(): void {
    this.results = [];
    // Removed localStorage save - results are now in database
    this.resultsSubject.next([]); // Notify subscribers
  }

  /**
   * Clear results for a specific session
   */
  clearSessionResults(sessionId: string): void {
    this.results = this.results.filter(r => r.sessionId !== sessionId);
    // Removed localStorage save - results are now in database
    this.resultsSubject.next([...this.results]); // Notify subscribers
  }

  /**
   * Export results as JSON
   */
  exportAsJSON(): string {
    return JSON.stringify(this.results, null, 2);
  }

  /**
   * Import results from JSON
   */
  importFromJSON(jsonString: string): boolean {
    try {
      const imported = JSON.parse(jsonString);
      if (Array.isArray(imported)) {
        this.results = imported;
        // Removed localStorage save - results are now in database
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get statistics
   */
  getStatistics() {
    const total = this.results.length;
    const passed = this.results.filter(r => r.status === 'success').length;
    const failed = this.results.filter(r => r.status === 'failed').length;
    const stopped = this.results.filter(r => r.status === 'stopped').length;

    const verificationResults = this.results.filter(r => r.passed !== undefined);
    const verificationPassed = verificationResults.filter(r => r.passed === true).length;
    const verificationFailed = verificationResults.filter(r => r.passed === false).length;

    return {
      total,
      passed,
      failed,
      stopped,
      passRate: total > 0 ? Math.round((passed / total) * 100) : 0,
      verificationTotal: verificationResults.length,
      verificationPassed,
      verificationFailed,
      verificationPassRate: verificationResults.length > 0
        ? Math.round((verificationPassed / verificationResults.length) * 100)
        : 0
    };
  }

  /**
   * Save to database via API
   */
  private saveExecutionResult(result: ExecutionResultRecord): void {
    try {
      // Validate required fields before saving to database
      if (!result.scenarioId) {
        return;
      }
      if (!result.functionalAreaId) {
        return;
      }

      let resultData = result.result;

      // Remove screenshot from resultData.screenshot if it exists
      if (resultData && typeof resultData === 'object' && 'screenshot' in resultData) {
        const { screenshot, ...resultWithoutScreenshot } = resultData;
        resultData = resultWithoutScreenshot;
      }

      // Remove screenshot from resultData.result.screenshot if it exists
      if (resultData && typeof resultData === 'object' && resultData.result && typeof resultData.result === 'object' && 'screenshot' in resultData.result) {
        const { screenshot, ...nestedResultWithoutScreenshot } = resultData.result;
        resultData = { ...resultData, result: nestedResultWithoutScreenshot };
      }

      const dbData: CreateExecutionResultRequest = {
        functionalAreaId: result.functionalAreaId,
        scenarioId: result.scenarioId,
        clientPackageMappingId: result.clientPackageMappingId,
        tenantId: result.tenantId,
        executionRunId: result.executionRunId,
        stepId: result.stepUuid || result.stepId,
        testDescription: result.description,
        status: result.status,
        startedAt: new Date(result.timestamp),
        endedAt: result.duration ? new Date(new Date(result.timestamp).getTime() + result.duration) : new Date(result.timestamp),


        logText: JSON.stringify({
          action: result.action,
          description: result.description,
          result: resultData,
          error: result.error,
          status: result.status,
          sessionId: result.sessionId,
          originalStepId: result.stepId,
          stepUuid: result.stepUuid,
          duration: result.duration,
          passed: result.passed,
          clientPackageMappingId: result.clientPackageMappingId // Include in logText as well
        }),
        // Include base64 screenshot in JSON payload for backends that support it
        screenshot: result.screenshot || ''
      };

      // If screenshot exists, convert to Blob and send via FormData to reduce payload size
      if (result.screenshot) {
        try {
          // Convert base64 data URL to Blob
          const base64Data = result.screenshot.replace(/^data:image\/\w+;base64,/, '');
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'image/png' });
          this.executionResultsApiService.createExecutionResultWithScreenshot(dbData, blob).subscribe({
            next: (savedResult) => {
            },
            error: (error) => {
            }
          });
        } catch (error) {
          this.executionResultsApiService.createExecutionResult(dbData).subscribe({
            next: (savedResult) => {
            },
            error: (err) => {
            }
          });
        }
      } else {
        // No screenshot - send regular JSON request
        this.executionResultsApiService.createExecutionResult(dbData).subscribe({
          next: (savedResult) => {
          },
          error: (error) => {
          }
        });
      }
    } catch (error) {
    }
  }

  /**
   *  NEW: Save precondition execution result with extended metadata
   */
  private saveExecutionResultWithPrecondition(
    result: ExecutionResultRecord,
    preconditionData: {
      success: boolean;
      authSuccess?: boolean;
      screenValidated?: boolean;
      error?: string;
      duration: number;
    }
  ): void {
    try {
      // Validate required fields
      if (!result.scenarioId || !result.functionalAreaId) {
        return;
      }

      const dbData: CreateExecutionResultRequest = {
        functionalAreaId: result.functionalAreaId,
        scenarioId: result.scenarioId,
        clientPackageMappingId: result.clientPackageMappingId,
        executionRunId: result.executionRunId,
        tenantId: result.tenantId,
        stepId: result.stepUuid || result.stepId,
        testDescription: result.description,
        status: result.status,
        startedAt: new Date(result.timestamp),
        endedAt: new Date(new Date(result.timestamp).getTime() + (result.duration || 0)),
        logText: JSON.stringify({
          action: result.action,
          description: result.description,
          status: result.status,
          sessionId: result.sessionId,
          duration: result.duration,
          passed: result.passed,
          preconditionExecuted: true,
          preconditionSuccess: preconditionData.success,
          authSuccess: preconditionData.authSuccess,
          screenValidated: preconditionData.screenValidated,
          preconditionError: preconditionData.error,
          preconditionDuration: preconditionData.duration
        }),
        screenshot: ''
      };

      this.executionResultsApiService.createExecutionResult(dbData).subscribe({
        next: (savedResult) => {
        },
        error: (error) => {
        }
      });
    } catch (error) {
    }
  }

  /**
   * Wait for all pending save operations to complete. Safe to call multiple times.
   */
  public async waitForPendingSaves(): Promise<void> {
    const saves = this.pendingSaves.splice(0);
    if (!saves.length) return;
    try { await Promise.allSettled(saves); } catch { }
  }

  /**
   * Capture screenshot from Electron (to be called from test executor)
   * Returns Base64 data URL string for storage in execution results
   */
  async captureScreenshot(): Promise<string | null> {
    try {
      const host: any = (window as any).host;
      if (!host?.captureScreenshot) return null;

      // Small pre-capture delay to ensure highlight overlay paints
      await new Promise(r => setTimeout(r, 120));

      const tryCapture = async (): Promise<string | null> => {
        try {
          // Optionally check page ready state
          if (host?.getPageState) {
            const stateResp = await host.getPageState();
            if (stateResp?.success && stateResp.state?.readyState === 'loading') {
              await new Promise(r => setTimeout(r, 250));
            }
          }
          const response = await host.captureScreenshot();
          if (response?.success && response?.screenshot) {
            return response.screenshot;
          }
        } catch { }
        return null;
      };

      // Retry up to 3 times with small backoff
      for (let i = 0; i < 3; i++) {
        const shot = await tryCapture();
        if (shot) return shot;
        await new Promise(r => setTimeout(r, 300 + i * 200));
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Capture screenshot as Uint8Array buffer for S3 upload
   * Converts the Base64 data URL to a binary buffer suitable for file uploads
   */
  async captureScreenshotBuffer(): Promise<Uint8Array | null> {
    try {
      // Call Electron host API to capture screenshot
      if ((window as any).host?.captureScreenshot) {
        const response = await (window as any).host.captureScreenshot();
        if (response.success && response.screenshot) {
          // Convert data URL to buffer (remove "data:image/png;base64," prefix)
          const base64Data = response.screenshot.replace(/^data:image\/\w+;base64,/, '');

          // Convert base64 to Uint8Array (browser-compatible)
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          return bytes;
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }
}